import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ColumnType } from "antd/es/table/interface";
import { Flex } from "antd";
import { DateTime } from "luxon";
import { useSelector } from "react-redux";

import { SurveyResponse } from "@/redux/survey/types";
import QueryTable from "@/components_v2/Tables/QueryTable";
import { useLazyGetSurveysQuery } from "@/redux/service";
import { SurveyStatus, SurveyType } from "@/shared/utils/constants";
import SurveyFilters from "@/features/survey/list/components/SurveyFilters";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import {
  SurveyFiltersState,
  SurveyTableProps,
} from "@/features/survey/list/types";

const SurveysTable = ({
  columnNames,
  filterNames,
  defaultFilters = {},
  additionalColumns,
  ...props
}: SurveyTableProps<SurveyResponse>) => {
  const { t } = useTranslation();

  const [filters, setFilters] =
    useState<Partial<SurveyFiltersState>>(defaultFilters);
  const currentUser = useSelector(selectCurrentUser);

  const columns: Record<string, ColumnType<SurveyResponse>> = useMemo(() => {
    const predefinedColumns: Record<string, ColumnType<SurveyResponse>> = {
      title: {
        title: t("survey.labels.title"),
        dataIndex: "title",
        ellipsis: true,
        width: 300,
        render: (value: string, survey) => {
          return <Link to={`/reports/${survey.id}`}>{value}</Link>;
        },
      },
      type: {
        title: t("survey.labels.type"),
        key: "type",
        dataIndex: "type",
        render: (value: string) => {
          return SurveyType.getItemByValue(value)?.label;
        },
      },
      startDate: {
        title: t("survey.labels.startDate"),
        key: "startDate",
        dataIndex: "startDate",
        render: (startDate: string) =>
          DateTime.fromISO(startDate).toFormat("dd MMMM yyyy HH:mm"),
      },
      deadline: {
        title: t("survey.labels.deadline"),
        dataIndex: "deadline",
        render: (_, survey) => {
          const now = DateTime.now();
          const hardDeadline = DateTime.fromISO(survey.hardDeadline);
          const isInGracePeriod =
            !!survey.softDeadline &&
            DateTime.fromISO(survey.softDeadline) < now &&
            now <= hardDeadline;
          const deadline =
            survey.softDeadline && !isInGracePeriod
              ? DateTime.fromISO(survey.softDeadline)
              : hardDeadline;

          return deadline.toFormat("dd MMMM yyyy HH:mm");
        },
      },
      isDue: {
        title: t("survey.labels.isDue"),
        dataIndex: "isDue",
        render: (_, record) => {
          return record.details.isSubmitted
            ? t("submitted")
            : record.details.isSubmissionRequired
              ? t("due")
              : t("notDue");
        },
      },
      status: {
        title: t("survey.labels.status"),
        dataIndex: "status",
        render: (value: string) => {
          return SurveyStatus.getItemByValue(value)?.label;
        },
      },
      invitesCount: {
        title: t("survey.labels.invitesCount"),
        key: "invitesCount",
        dataIndex: "invitesCount",
      },
      responsesCount: {
        title: t("survey.labels.responsesCount"),
        key: "responsesCount",
        dataIndex: "responsesCount",
      },
    };
    return { ...predefinedColumns, ...(additionalColumns || {}) };
  }, []);

  const mappedColumns = useMemo(
    () =>
      columnNames.map(
        (columnKey) => columns[columnKey] as ColumnType<SurveyResponse>,
      ),
    [columns, columnNames],
  );

  return (
    currentUser && (
      <Flex vertical gap="small">
        <SurveyFilters
          filterNames={filterNames}
          setFilters={setFilters}
          defaultFilters={defaultFilters}
        />
        <QueryTable<SurveyResponse>
          query={useLazyGetSurveysQuery}
          filters={filters}
          initialSorting={{
            field: "startDate",
            order: "descend",
          }}
          columns={mappedColumns}
          {...props}
        />
      </Flex>
    )
  );
};

export default SurveysTable;
